/* eslint-disable no-console */
/* eslint-disable  @typescript-eslint/no-explicit-any */
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogger } from '@fanduel/casino-datadog-logger';

// Error event constants
export const sdkFailedToRetrieveLicenseEvent =
  'SDK failed to retrieve license via API';
export const sdkFailedToRetrieveDecryptGeoEvent =
  'SDK failed to retrieve decrypted geopacket via API';
export const sdkFailedToCreateJWTEvent = 'SDK failed to create JWT via API';
export const sdkFailedToRetrieveIPAddressEvent =
  'SDK failed to retrieve IP address via API';
export const sdkFailedToRetrieveSolutionParametersEvent =
  'SDK failed to retrieve solution parameters via API';
export const sdkReceivedInvalidSolutionParametersViaAPI =
  'SDK received invalid solution parameters via API';
export const geoComplyFailedToReturnTokenEvent =
  'GeoComply failed to return geotoken to SDK';
export const sdkFailedToCallGeoComplyEvent = 'SDK failed to call GeoComply';
export const geoComplyReturnedBlankTokenEvent =
  'GeoComply returned blank token to SDK';
export const geoComplyIpServiceFailedEvent = 'GeoComply IP service failed';
export const amplitudeExperimentFailedToStartEvent =
  'Amplitude experiment failed to start';

// Mapping of error event constants to display names
export const errorEventDisplayNames: Record<string, string> = {
  [sdkFailedToRetrieveLicenseEvent]: 'Failed to retrieve license',
  [sdkFailedToRetrieveDecryptGeoEvent]: 'Failed to decrypt geopacket',
  [sdkFailedToCreateJWTEvent]: 'Failed to create JWT',
  [sdkFailedToRetrieveIPAddressEvent]: 'Failed to retrieve IP address',
  [sdkFailedToRetrieveSolutionParametersEvent]:
    'Failed to retrieve solution parameters',
  [sdkReceivedInvalidSolutionParametersViaAPI]:
    'Invalid solution parameters received',
  [geoComplyFailedToReturnTokenEvent]: 'GeoComply did not return geotoken',
  [sdkFailedToCallGeoComplyEvent]: 'Failed to call GeoComply',
  [geoComplyReturnedBlankTokenEvent]: 'GeoComply returned blank token',
  [geoComplyIpServiceFailedEvent]: 'GeoComply IP service failed',
  [amplitudeExperimentFailedToStartEvent]:
    'Amplitude experiment failed to start',
};

export enum DdEventCategory {
  Geolocation = 'GEOLOCATION',
  FetchFgp = 'FETCH_FGP',
  GameContext = 'GAME_CONTEXT',
  ProviderIntegration = 'PROVIDER_INTEGRATION',
  ProviderMessages = 'PROVIDER_MESSAGES',
  Initialization = 'CLIENT_INITIALIZATION',
  UpdateGeolocation = 'UPDATE_GEOLOCATION',
  UpdateAuthentication = 'UPDATE_AUTHENTICATION',
  WraperMessages = 'WRAPPER_MESSAGES',
}

export enum DdEventType {
  Success = 'SUCCESS',
  Error = 'ERROR',
  Info = 'INFO',
}

export type DdEvent = {
  name: string;
  description?: string;
  payload?: Record<string, unknown>;
  useCase: DdEventCategory;
  eventType: DdEventType;
};

export type DdAction = DdEvent & {
  context?: Record<string, unknown>;
  service?: string;
};

export type DdError = DdEvent & {
  context?: Record<string, any>;
  stack?: string;
  service?: string;
};

export type DatadogSite =
  | 'datadoghq.com'
  | 'datadoghq.eu'
  | 'us3.datadoghq.com'
  | 'us5.datadoghq.com'
  | 'ddog-gov.com';
export class Logger {
  static iosVersion: string | undefined;

  static provider: string | undefined;

  static gameStudio: string | undefined;

  static jackpotMode: string | undefined;

  static region: string | undefined;

  static channel: string | undefined;

  static experiments: Record<string, unknown>;

  static initializeLoggerContext(context: {
    experiments: Record<string, unknown> | undefined;
    version: string | undefined;
    provider: string | undefined;
    gameStudio: string | undefined;
    jackpotMode: string | undefined;
    region: string | undefined;
    channel: string | undefined;
  }) {
    this.iosVersion = context?.version;
    this.experiments = context?.experiments || {};
    this.provider = context?.provider;
    this.gameStudio = context?.gameStudio;
    this.jackpotMode = context?.jackpotMode;
    this.region = context?.region;
    this.channel = context?.channel;

    if (context?.region) {
      datadogRum.setGlobalContextProperty('region', context.region);
    }
  }

  static rumAction(action: DdAction) {
    datadogLogger.ddRumAction({
      ...action,
      experiments: this.experiments,
      payload: {
        ...action.payload,
        iosVersion: this.iosVersion,
        gameStudio: this.gameStudio,
        provider: this.provider,
        jackpotMode: this.jackpotMode,
        channel: this.channel,
      },
    });
  }

  static rumError(error: DdError) {
    const { context } = error;

    if (typeof context?.error?.config?.data === 'string') {
      const dataObj = JSON.parse(context.error.config.data);
      delete dataObj?.loginToken;
      delete dataObj?.token;
      delete dataObj?.authToken;
      delete context.error.config.data;
      context.error.config.data = dataObj;
    }

    datadogLogger.ddRumError({
      ...error,
      experiments: this.experiments,
      payload: {
        ...error.payload,
        iosVersion: this.iosVersion,
      },
    });
  }

  static logRumAction(action: DdAction) {
    const { description, name, context, useCase, eventType, service } = action;

    if (process.env.DISABLE_LOGGING === 'true') {
      console.log('LOG ACTION - ', { action });
    } else {
      // DD logs for accurate tracing
      datadogLogger.ddRumLog({
        name: `[${useCase}] - ${name}`,
        payload: {
          description,
          context,
          useCase,
          eventType,
          service,
          iosVersion: this.iosVersion,
          gameStudio: this.gameStudio,
          provider: this.provider,
          jackpotMode: this.jackpotMode,
          region: this.region,
          channel: this.channel,
        },
      });

      // Rum events for accurate debuging
      this.rumAction(action);
    }
  }

  static logRumError(error: DdError) {
    const { description, name, context, stack, useCase, eventType, service } =
      error;

    if (typeof context?.error?.config?.data === 'string') {
      const dataObj = JSON.parse(context.error.config.data);
      delete dataObj?.loginToken;
      delete dataObj?.token;
      delete dataObj?.authToken;
      delete context.error.config.data;
      context.error.config.data = dataObj;
    }

    if (process.env.DISABLE_LOGGING === 'true') {
      console.log('LOG ERROR - ', { error });
    } else {
      // DD logs for accurate tracing
      datadogLogger.ddRumErrorLog({
        name: `[${useCase}] - ${name}`,
        payload: {
          ...context,
          description,
          stack,
          eventType,
          useCase,
          errorCode: `${eventType}_${useCase}`,
          service,
          gameStudio: this.gameStudio,
          provider: this.provider,
          iosVersion: this.iosVersion,
          region: this.region,
          channel: this.channel,
        },
      });

      // Rum events for accurate debuging
      datadogLogger.ddRumError({
        ...error,
        payload: {
          ...error.payload,
          gameStudio: this.gameStudio,
          provider: this.provider,
          channel: this.channel,
        },
      });
    }
  }
}
